import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { AutoPricingInputs, Feature } from 'src/WebApiController';

import * as styles from './CompAdvancedPricingSettingsContainer.css';

export const CompAdvancedPricingSettingsInclude = () => {
  const hasUnmappedInvToggle = useUserHasFeature(Feature.UnmappedInvToggle);

  return (
    <Stack direction="column" gap="s">
      <CompIncludeSettingsCheckbox
        name="compListingExcludeFanInventory"
        title={ContentId.FanInventory}
        description={ContentId.AutopriceAgainstAllStubHubListings}
      />
      <CompIncludeSettingsCheckbox
        name="compListingExcludeDefects"
        title={ContentId.Defects}
        description={ContentId.AutopriceAgainstInventoryWithMatchingDefects}
      />
      {hasUnmappedInvToggle && (
        <CompIncludeSettingsCheckboxPositive
          name="compListingIncludeUnmappedInventory"
          title={ContentId.UnmappedInventory}
          description={ContentId.AutopriceAgainstUnmappedInventory}
        />
      )}
    </Stack>
  );
};

const CompIncludeSettingsCheckbox = ({
  name,
  title,
  description,
}: {
  name: 'compListingExcludeFanInventory' | 'compListingExcludeDefects';
  title: ContentId;
  description: ContentId;
}) => {
  const { watch, setValue } = useFormContext<AutoPricingInputs>();
  const isNotChecked = watch(name);
  const isChecked = !isNotChecked;

  return (
    <Stack
      className={clsx(styles.settingOptionContainer, {
        [styles.settingOptionContainerSelected]: isChecked,
      })}
    >
      <Checkbox
        checked={isChecked}
        onChange={(e) => setValue(name, !e.target.checked)}
      />
      <Stack direction="column" gap="s">
        <div
          className={clsx(styles.settingOptionTitle, {
            [styles.settingOptionTitleSelected]: isChecked,
          })}
        >
          <Content id={title} />
        </div>

        <div
          className={clsx(styles.settingOptionDescription, {
            [styles.settingOptionDescriptionSelected]: isChecked,
          })}
        >
          <Content id={description} />
        </div>
      </Stack>
    </Stack>
  );
};

// Checkbox for positive-named fields (IncludeXxx) - no inversion needed
const CompIncludeSettingsCheckboxPositive = ({
  name,
  title,
  description,
}: {
  name: 'compListingIncludeUnmappedInventory';
  title: ContentId;
  description: ContentId;
}) => {
  const { watch, setValue } = useFormContext<AutoPricingInputs>();
  const isChecked = watch(name) ?? false;

  return (
    <Stack
      className={clsx(styles.settingOptionContainer, {
        [styles.settingOptionContainerSelected]: isChecked,
      })}
    >
      <Checkbox
        checked={isChecked}
        onChange={(e) => setValue(name, e.target.checked)}
      />
      <Stack direction="column" gap="s">
        <div
          className={clsx(styles.settingOptionTitle, {
            [styles.settingOptionTitleSelected]: isChecked,
          })}
        >
          <Content id={title} />
        </div>

        <div
          className={clsx(styles.settingOptionDescription, {
            [styles.settingOptionDescriptionSelected]: isChecked,
          })}
        >
          <Content id={description} />
        </div>
      </Stack>
    </Stack>
  );
};
